import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { ButtonDropdown } from "@rio-cloud/rio-uikit";

import { capitalize } from "../../../utils/stringUtils";

import { Occurrence } from "../../../models/occurrence";
import { Scheduling } from "../../../models/schedule";

import { formatOccurrenceStepLabelByLabel, OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../../enums/occurrenceStep";

import CallForm from "../components/form/callForm";
import ScreeningForm from "../components/form/screeningForm";
import DisplacementForm from "../components/form/displacementForm";
import SecondDiagnosisForm from "../components/form/secondDiagnosisForm";
import RemovalForm from "../components/form/removalForm";
import ThirdDiagnosisForm from "../components/form/thirdDiagnosisForm";
import FourthDiagnosisForm from "../components/form/fourthDiagnosisForm";
import AnalysisWarrantyForm from "../components/form/analysisWarrantyForm";
import CustomerOkForm from "../components/form/customerOkForm";
import PartsForm from "../components/form/partsForm";
import PartsInTransitForm from "../components/form/partsInTransitForm";
import AwaitingRepairForm from "../components/form/awaitingRepairForm";
import RepairForm from "../components/form/repairForm";
import ReleaseForm from "../components/form/releaseForm";

import { fetchSchedulingByChassisList } from "../../../api/fetchScheduling";

import "../styles.scss";

interface ISummaryDialogProps {
    showDialog: boolean;
    occurrence: Occurrence;
    isValidStep: (requiredFields: string[]) => boolean;
    loading: boolean;
    save: () => Promise<Occurrence | false>;
    onClose: () => void;
    disabledAllFields?: boolean;
    persona: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
    finalized?: boolean;
    occurrenceStepAvailable: string[];
    partners?: { id: string, name: string }[];
    changeStep?: (occurrence: Occurrence, fromStepId: number, to: OccurrenceStepEnum) => Promise<Occurrence | null | undefined>;
    getNotFilledRequiredFields: (requiredFields: string[], occurrence: Occurrence) => string[];
}

const SummaryDialog = (props: ISummaryDialogProps) => {
    const intl = useIntl();
    const [step, setStep] = useState<OccurrenceStepLabelEnum | undefined>(OccurrenceStepLabelEnum.CALL);
    const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);

    useEffect(() => {
        const fetchStep = async () => {
            if (props.occurrence?.scheduleNumber) {
                const scheduling = (await fetchSchedulingByChassisList([props.occurrence.vehicleChassis])).find(e => e.scheduleNumber === props.occurrence.scheduleNumber);
                setScheduling(scheduling);
            }
        }
        if (props.showDialog) fetchStep();
    }, [props.showDialog, props.occurrence?.scheduleNumber]);

    const formProps = {
        occurrence: props.occurrence,
        setOccurrenceValue: () => { },
        setOccurrenceStepValue: () => { },
        step: props.occurrence?.steps?.find((s) => s.id === step || s.name === step),
        summaryStep: props.occurrence?.steps?.find((s) => s.id === step || s.name === step),
        isValidStep: props.isValidStep,
        loading: props.loading,
        save: props.save,
        onClose: props.onClose,
        disabledAllFields: true,
        persona: props.persona,
        finalized: props.finalized,
        occurrenceStepAvailable: props.occurrenceStepAvailable,
        partners: props.partners,
        hiddenSaveButton: true,
        changeStep: props.changeStep,
        getNotFilledRequiredFields: props.getNotFilledRequiredFields,
        scheduling: scheduling,
    }

    const form = () => {
        if (!step) return null;

        switch (step) {
            case OccurrenceStepLabelEnum.CALL:
                return <CallForm {...formProps} />;

            case OccurrenceStepLabelEnum.SCREENING:
                return <ScreeningForm {...formProps} />;

            case OccurrenceStepLabelEnum.DISPLACEMENT:
                return <DisplacementForm {...formProps} />;

            case OccurrenceStepLabelEnum.SECOND_DIAGNOSIS:
                return <SecondDiagnosisForm {...formProps} />;

            case OccurrenceStepLabelEnum.REMOVAL:
                return <RemovalForm {...formProps} />;

            case OccurrenceStepLabelEnum.THIRD_DIAGNOSIS:
                return <ThirdDiagnosisForm {...formProps} />;

            case OccurrenceStepLabelEnum.FOURTH_DIAGNOSIS:
                return <FourthDiagnosisForm {...formProps} />;

            case OccurrenceStepLabelEnum.ANALYSIS_WARRANTY:
                return <AnalysisWarrantyForm {...formProps} />;

            case OccurrenceStepLabelEnum.CUSTOMER_OK:
                return <CustomerOkForm {...formProps} />;

            case OccurrenceStepLabelEnum.PARTS:
                return <PartsForm {...formProps} />;

            case OccurrenceStepLabelEnum.PARTS_IN_TRANSIT:
                return <PartsInTransitForm {...formProps} />;

            case OccurrenceStepLabelEnum.AWAITING_REPAIR:
                return <AwaitingRepairForm {...formProps} />;

            case OccurrenceStepLabelEnum.REPAIR:
                return <RepairForm {...formProps} />;

            case OccurrenceStepLabelEnum.RELEASE:
                return <ReleaseForm {...formProps} />;

            default:
                return null;
        }
    }

    const mobileStepMenu = () => {
        const [isOpen, setIsOpen] = useState(false);

        const mobileMenuItems = props.occurrenceStepAvailable
            .map(childStep => OccurrenceStepLabelEnum[childStep as keyof typeof OccurrenceStepLabelEnum])
            .slice(0, 8)
            .filter((childStep) => childStep !== null)
            .map((childStep) => ({
                label: capitalize(formatOccurrenceStepLabelByLabel(intl, childStep ?? '')),
                value: childStep,
                onSelect: () => setStep(childStep)
            }));

        return (
            <div>
                <ButtonDropdown
                    title={intl.formatMessage({ id: "intl-msg:kanban.summaryDialog.selectStep" })}
                    items={mobileMenuItems}
                    open={isOpen}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                />
            </div>
        );
    }

    return (
        props.showDialog &&
        <>
            <div className="bg-kanban-sidebar" onClick={props.onClose}></div>
            <div className="kanban-sidebar">
                <div className="flex justify-between items-center px-6">
                    <div className="text-size-h4 p-4">
                        {intl.formatMessage({ id: "intl-msg:kanban.summaryDialog.title" })}
                    </div>

                    <div className="p-4 text-size-h5 padding-right-10 cursor-pointer">
                        <span className="rioglyph rioglyph-remove" onClick={props.onClose}></span>
                    </div>
                </div>
                <hr className="border-color-gray-light border-bottom-solid mb-6" />

                {isMobile() ? mobileStepMenu() : (
                    <div className="px-6">
                        <ul className="nav nav-tabs nav-justified">
                            {
                                props.occurrenceStepAvailable
                                    .map(childStep => OccurrenceStepLabelEnum[childStep as keyof typeof OccurrenceStepLabelEnum])
                                    .slice(0, 8)
                                    .filter((childStep) => childStep !== null)
                                    .map((childStep) => (
                                        <li key={childStep} className={`nav-item ${childStep === step ? "active" : ""}`}>
                                            <a href="#" className="cursor-pointer" onClick={() => setStep(childStep)}>{formatOccurrenceStepLabelByLabel(intl, childStep)}</a>
                                        </li>
                                    ))
                            }
                        </ul>
                        <div className="h-[10px] w-full" />
                        <ul className="nav nav-tabs nav-justified mt-4">
                            {
                                props.occurrenceStepAvailable
                                    .map(childStep => OccurrenceStepLabelEnum[childStep as keyof typeof OccurrenceStepLabelEnum])
                                    .slice(8)
                                    .filter((childStep) => childStep !== null)
                                    .map((childStep) => (
                                        <li key={childStep} className={`nav-item ${childStep === step ? "active" : ""}`}>
                                            <a href="#" className="cursor-pointer" onClick={() => setStep(childStep)}>{capitalize(formatOccurrenceStepLabelByLabel(intl, childStep ?? ''))}</a>
                                        </li>
                                    ))
                            }
                        </ul>
                    </div>
                )}
                <div className="tab-content overflow-y-auto h-[calc(100vh-180px)]">
                    {props.occurrence ? form() : <div className="flex items-center justify-center text-center h-full">
                        <div className="flex"><div className="spinner mr-4"><div /><div /><div /><div /><div /><div /><div /><div /></div>{intl.formatMessage({ id: "intl-msg:kanban.summaryDialog.loading" })}</div>
                    </div>}
                </div>
            </div>
        </>
    )
};

export default SummaryDialog;